import { ContentItemViewNodesType } from '../../../types'
import { ParentOrderCalculation } from '../types'
import {
    findParentNode,
    findSiblings,
    findValidDemoteParent,
} from './nodeHelpers'

/**
 * Calculates the new parent and order for promotion
 * @param currentParent - The current parent of the node
 * @param nodes - All available nodes
 * @param paragraphLevel - The current paragraph level
 * @returns The new parent and order position
 */
export const calculatePromoteVariables = (
    currentParent: Partial<ContentItemViewNodesType> | undefined,
    nodes: Partial<ContentItemViewNodesType>[],
    paragraphLevel: number
): ParentOrderCalculation => {
    if (!currentParent) {
        return { newParent: undefined, order: 0 }
    }

    // special case: Sustainability content at level 2
    const isSustainabilityPromotion =
        currentParent.partType === 'ContentGroup' && paragraphLevel === 2

    if (isSustainabilityPromotion) {
        // find position among top-level sustainability items
        const topLevelItems = nodes.filter(
            (n) => n.order === 1 && n.partType === 'ContentGroup'
        )
        return { newParent: undefined, order: topLevelItems.length }
    }

    const grandparent = findParentNode(nodes, currentParent)
    
    // find parent's position and place after it
    const parentOrder = Math.max(0, currentParent.order ?? 0)
    const order = parentOrder + 1

    return { newParent: grandparent, order }
}

/**
 * Calculates the new parent and order for demotion
 * @param currentNode - The node being demoted
 * @param currentParent - The current parent of the node
 * @param nodes - All available nodes
 * @returns The new parent and order position
 */
export const calculateDemoteVariables = (
    currentNode: Partial<ContentItemViewNodesType>,
    currentParent: Partial<ContentItemViewNodesType> | undefined,
    nodes: Partial<ContentItemViewNodesType>[]
): ParentOrderCalculation => {
    const siblings = findSiblings(nodes, currentParent, currentNode.id)
    const newParent = findValidDemoteParent(siblings, currentNode)

    if (!newParent) {
        return { newParent: undefined, order: 0 }
    }

    // Place at end of new parent's children
    const newParentChildren = findSiblings(nodes, newParent)
    const order =
        newParentChildren.length > 0 ? newParentChildren.length + 1 : 0

    return { newParent, order }
}
